/**
 * Money formatters for the invoices vertical (and reusable elsewhere).
 *
 * Wire shape is **stringified bigint paise** (`'1234500'`). The
 * helpers here parse that, do all math in bigint (no float drift),
 * and produce display strings with **Indian thousands grouping**
 * (last 3 digits, then groups of 2): `1234500` → `12,345.00`.
 *
 * `formatRupees` is the canonical "₹12,345.00" string — what every
 * cell, line total, and grand-total label uses.
 *
 * `formatLakhCrore` is the compact form for KPI tiles
 * (`₹12.50L`, `₹1.25Cr`) where the table-style number would crowd.
 *
 * Pure functions; no React, no `Intl.NumberFormat` (which doesn't
 * cleanly handle the lakh/crore breakpoint and doesn't accept
 * bigint). Tests pin the edge cases.
 */

/** Parse a wire-string of paise into a bigint. Coerces blank → 0n. */
export function parsePaise(paiseStr: string | bigint | null | undefined): bigint {
  if (paiseStr === null || paiseStr === undefined || paiseStr === "") return 0n;
  if (typeof paiseStr === "bigint") return paiseStr;
  return BigInt(paiseStr);
}

/**
 * `1234500` paise → `12,345.00`. No currency prefix — the caller
 * adds `₹` (or skips it for raw cells). Indian thousands grouping.
 */
export function formatPaise(paise: string | bigint | null | undefined): string {
  const value = parsePaise(paise);
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const rupees = abs / 100n;
  const remainder = abs % 100n;
  const padded = String(remainder).padStart(2, "0");
  const r = rupees.toString();
  const head = r.slice(0, -3);
  const tail = r.slice(-3);
  const groupedHead = head ? head.replace(/(\d)(?=(\d{2})+$)/g, "$1,") : "";
  const grouped = groupedHead ? `${groupedHead},${tail}` : tail;
  return `${negative ? "-" : ""}${grouped}.${padded}`;
}

/** `12345600` → `₹1,23,456.00`. Adds the rupee sign. */
export function formatRupees(paise: string | bigint | null | undefined): string {
  return `₹${formatPaise(paise)}`;
}

/**
 * Compact lakh/crore for KPI tiles. Breakpoints:
 *   - >= 1 crore (1e9 paise)  → `₹X.XXCr`
 *   - >= 1 lakh  (1e7 paise)  → `₹X.XXL`
 *   - >= 1k rupees           → `₹XX,XXX`
 *   - else                    → `₹X.XX`
 *
 * One decimal place for crore/lakh keeps the tile readable without
 * losing too much precision; round-half-to-even on the cents.
 */
export function formatLakhCrore(paise: string | bigint | null | undefined): string {
  const value = parsePaise(paise);
  const abs = value < 0n ? -value : value;
  const negativePrefix = value < 0n ? "-" : "";

  const oneCroreInPaise = 10_000_000_00n; // 1 Cr = 1e7 rupees = 1e9 paise
  const oneLakhInPaise = 100_000_00n; // 1 L = 1e5 rupees = 1e7 paise

  if (abs >= oneCroreInPaise) {
    return `${negativePrefix}₹${dividedToOneDecimal(abs, oneCroreInPaise)}Cr`;
  }
  if (abs >= oneLakhInPaise) {
    return `${negativePrefix}₹${dividedToOneDecimal(abs, oneLakhInPaise)}L`;
  }
  return `${negativePrefix}${formatRupees(abs).replace(/\.\d{2}$/, "")}`;
}

/** Format milli-quantity wire values (`1000` -> `1`, `1500` -> `1.5`). */
export function qtyMilliFormat(qtyMilli: number): string {
  if (qtyMilli % 1000 === 0) return String(qtyMilli / 1000);
  return (qtyMilli / 1000).toLocaleString("en-IN", {
    maximumFractionDigits: 3,
    minimumFractionDigits: 0,
  });
}

/** `1234567n / 100000n → '12.3'`. Bigint-only one-decimal divide. */
function dividedToOneDecimal(numerator: bigint, denominator: bigint): string {
  const tenths = (numerator * 10n) / denominator; // truncates
  const whole = tenths / 10n;
  const frac = tenths % 10n;
  return `${whole}.${frac}`;
}

/**
 * Convert a user-typed rupees amount (`'42500'` / `'42500.00'`) to
 * paise as a bigint. Strips commas + the rupee sign. Used by the
 * line-item rate input.
 *
 * Rejects malformed input by returning `null`. Empty / `'-'` is `0n`.
 */
export function parseRupeesToPaise(input: string): bigint | null {
  const cleaned = input.replace(/[₹,\s]/g, "").trim();
  if (cleaned === "" || cleaned === "-") return 0n;
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ""] = cleaned.split(".");
  const padded = (fracPart + "00").slice(0, 2);
  return BigInt(intPart || "0") * 100n + BigInt(padded || "0");
}
